import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Keyboard, Camera, Upload, Zap } from 'lucide-react';

/**
 * ScanModeSelector - Component for selecting barcode scan input mode
 * 
 * Modes:
 * - scan: Type/paste barcode manually (fastest with USB scanner)
 * - camera: Use device camera to scan
 * - manual: Manual product entry without barcode
 */
const ScanModeSelector = ({ 
  mode, 
  onModeChange, 
  showOnboarding = false,
  onDismissOnboarding,
  isNewUser = false,
  t = (key, fallback) => fallback 
}) => {
  return (
    <div className="space-y-4">
      {/* Onboarding Tip for New Users */}
      {showOnboarding && isNewUser && (
        <div className="relative bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
          <button 
            onClick={onDismissOnboarding}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-sm"
          >
            ✕
          </button>
          <div className="flex items-start gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Zap className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold text-emerald-900">Quick Start Guide</h4>
              <p className="text-sm text-emerald-700 mt-1">
                1. Scan or type at least <strong>2 items</strong> to create a deal batch<br/>
                2. Review and adjust prices, then post to notify local shoppers<br/>
                3. Tip: Use a USB barcode scanner for fastest entry!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mode Selection Buttons */}
      <div className="grid grid-cols-3 gap-3">
        <Button
          variant={mode === 'scan' ? 'default' : 'outline'}
          onClick={() => onModeChange('scan')}
          className={`h-24 flex flex-col items-center justify-center gap-2 transition-all ${
            mode === 'scan' ? 'ring-2 ring-blue-400 ring-offset-2' : ''
          }`}
          data-testid="scan-mode-keyboard"
        >
          <Keyboard className="w-6 h-6" />
          <span className="text-sm font-medium">{t('scanner_ui.type_code', 'Type Code')}</span>
          <span className="text-xs text-gray-500">Fastest</span>
        </Button>
        
        <Button
          variant={mode === 'camera' ? 'default' : 'outline'}
          onClick={() => onModeChange('camera')}
          className={`h-24 flex flex-col items-center justify-center gap-2 transition-all ${
            mode === 'camera' ? 'ring-2 ring-blue-400 ring-offset-2' : ''
          }`}
          data-testid="scan-mode-camera"
        >
          <Camera className="w-6 h-6" />
          <span className="text-sm font-medium">{t('scanner_ui.camera', 'Camera')}</span>
          <span className="text-xs text-gray-500">Point & Scan</span>
        </Button>
        
        <Button
          variant="outline"
          onClick={() => onModeChange('manual')}
          className="h-24 flex flex-col items-center justify-center gap-2 hover:bg-gray-50 transition-all"
          data-testid="scan-mode-manual"
        >
          <Upload className="w-6 h-6" />
          <span className="text-sm font-medium">{t('scanner_ui.manual_entry', 'Manual Entry')}</span>
          <span className="text-xs text-gray-500">No Barcode</span>
        </Button>
      </div>
    </div>
  );
};

export default ScanModeSelector;
